import React, { useState } from "react";
import {
  Card,
  Checkbox,
  Row,
  Col,
  Table,
  Form,
  Input,
  Popconfirm,
  Typography,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Button } from "react-bootstrap";
import NavBar from "../NavBar/navbar";
import "./rolemanagement.css";
import AxiosHelper from "../../helper/axios-helper";
import { toast, ToastContainer } from "react-toastify";
import { canAccessPage, isAdmin } from "../../helper/token";
import { useNavigate } from "react-router-dom";

const RoleManagementPage = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(-1);
  const [selectedAdmin, setSelectedAdmin] = useState(false);

  const [roles, setRoles] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  const [form] = Form.useForm();

  const isEditing = (record) => record.id === editingRole;

  const [customers, setCustomers] = useState([]);

  const [users, setUsers] = useState([]);

  const [screens, setScreens] = useState([]);

  const edit = (record) => {
    form.setFieldsValue({
      name: record.name,
      description: record.description,
    });
    setEditedName(record.name);
    setEditedDescription(record.description);
    setEditingRole(record.id);
  };

  const cancel = () => {
    setEditingRole(null);
  };

  const handleSaveRole = async (record) => {
    try {
      const updatedName = editedName;
      const updatedDescription = editedDescription;

      const updateRole = {
        RoleId: record.id,
        RoleDescription: updatedDescription,
        RoleName: updatedName,
        RoleAdmin: record.active,
      };

      AxiosHelper.makeAxiosRequest(
        "Update",
        "RoleManagement",
        "UpdateRole",
        null,
        updateRole
      )
        .then(() => {
          notifySuccessful("Successfully updated Role");
          const newRoles = roles.map((role) => {
            if (role.id === record.id) {
              return {
                ...role,
                name: updatedName,
                description: updatedDescription,
              };
            }
            return role;
          });
          setRoles(newRoles);
          cancel();
        })
        .catch((error) => {
          notifyFailed("Could not update Role. Please try again.");
          console.log(error);
        });
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: "Admin",
      dataIndex: "active",
      width: "10%",
      render: (text, record) => {
        return (
          <Checkbox
            checked={text}
            onChange={(e) => handleAdminToggle(record.id, e.target.checked)}
            style={isAdmin() ? {} : { opacity: "50%" }}
          />
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      width: "25%",
      render: (text, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="name"
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: "Please input a name!",
              },
            ]}
          >
            <Input
              defaultValue={editedName}
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
          </Form.Item>
        ) : (
          <span className="pointer-cursor">{text}</span>
        );
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      width: "30%",
      render: (text, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item name="description" style={{ margin: 0 }}>
            <Input
              defaultValue={editedDescription}
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
            />
          </Form.Item>
        ) : (
          <span className="pointer-cursor">{text}</span>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: "15%",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => handleSaveRole(record)}
              style={{
                marginRight: "25%",
              }}
            >
              <SaveOutlined />
            </Typography.Link>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <a>
                <CloseCircleOutlined />
              </a>
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Typography.Link
              disabled={editingRole !== null}
              onClick={() => edit(record)}
              style={{
                marginRight: "30%",
              }}
            >
              <EditOutlined />
            </Typography.Link>
            {record.active ? (
              <DeleteOutlined
                style={
                  record.active ? { opacity: "50%", cursor: "default" } : {}
                }
              />
            ) : (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => handleDeleteRole(record.id)}
              >
                <DeleteOutlined />
              </Popconfirm>
            )}
          </span>
        );
      },
    },
  ];

  const notifySuccessful = (message) => {
    toast.success(message, {
      position: "bottom-center",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const notifyFailed = (message) => {
    toast.error(message, {
      position: "bottom-center",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleDeleteRole = (roleId) => {
    AxiosHelper.makeAxiosRequest(
      "Delete",
      "RoleManagement",
      "DeleteRole",
      { roleId: roleId },
      null
    )
      .then(() => {
        notifySuccessful("Successfully deleted Role");
        let newRoles = [...roles];
        newRoles = newRoles.filter((role) => role.id !== roleId);
        setRoles(newRoles);
      })
      .catch((error) => {
        notifyFailed("Could not delete Role. Please try again");
        console.log(error);
      });
  };

  const updateCustomersWithIds = (customerIds) => {
    const updatedCustomers = customers.map((customer) => ({ ...customer }));

    if (customerIds.length === 0) {
      updatedCustomers.forEach((updatedCustomer) => {
        updatedCustomer.included = false;
      });
    }

    customerIds.forEach((customerId) => {
      updatedCustomers.forEach((updatedCustomer) => {
        if (updatedCustomer.id === customerId) {
          updatedCustomer.included = !updatedCustomer.included;
        }
      });
    });

    setCustomers(updatedCustomers);
    return updatedCustomers;
  };

  const updateUsersWithIds = (userIds) => {
    const updatedUsers = users.map((user) => ({ ...user }));

    if (userIds.length === 0) {
      updatedUsers.forEach((updatedUser) => {
        updatedUser.included = false;
      });
    }

    userIds.forEach((userId) => {
      updatedUsers.forEach((updatedUser) => {
        if (updatedUser.id === userId) {
          updatedUser.included = !updatedUser.included;
        }
      });
    });

    setUsers(updatedUsers);
    return updatedUsers;
  };

  const updateScreensWithIds = (screenIds) => {
    const updatedScreens = screens.map((screen) => ({ ...screen }));

    if (screenIds.length === 0) {
      updatedScreens.forEach((updatedScreen) => {
        updatedScreen.included = false;
      });
    }

    screenIds.forEach((screenId) => {
      updatedScreens.forEach((updatedScreen) => {
        if (updatedScreen.id === screenId) {
          updatedScreen.included = !updatedScreen.included;
        }
      });
    });

    setScreens(updatedScreens);
    return updatedScreens;
  };

  const fetchRoleData = (roleId) => {
    AxiosHelper.makeAxiosRequest(
      "Read",
      "RoleManagement",
      "GetRoleIncludedData",
      { roleId: roleId },
      null
    )
      .then((response) => {
        updateCustomersWithIds(response.data.customerIds);
        updateScreensWithIds(response.data.pageIds);
        updateUsersWithIds(response.data.userIds);
      })
      .catch((error) => {
        console.log(error);
        notifyFailed("Could not read the data. Please try again.");
      });
  };

  const clear = () => {
    const updatedCustomers = customers.map((customer) => ({
      ...customer,
      included: false, // Reset the 'included' property for customers
    }));

    const updatedUsers = users.map((user) => ({
      ...user,
      included: false, // Reset the 'included' property for users
    }));

    const updatedScreens = screens.map((screen) => ({
      ...screen,
      included: false, // Reset the 'included' property for screens
    }));

    setCustomers(updatedCustomers);
    setUsers(updatedUsers);
    setScreens(updatedScreens);
  };

  const handleSelectRole = async (roleId) => {
    if (selectedRole !== roleId) {
      clear();
      setSelectedRole(roleId);
      roles.forEach((role) => {
        if (role.id === roleId) {
          setSelectedAdmin(role.active);
        }
      });
    }
  };

  const handleAddRole = () => {
    const currentRoles = [...roles];
    const newRole = {
      id: 0,
      name: "",
      description: "",
      active: false,
    };

    const newRoleApi = {
      id: 0,
      RoleDescription: "",
      RoleName: "",
      RoleAdmin: false,
    };

    AxiosHelper.makeAxiosRequest(
      "Update",
      "RoleManagement",
      "AddRole",
      null,
      newRoleApi
    )
      .then((response) => {
        newRole.id = response.data;
        currentRoles.push(newRole);
        setRoles(currentRoles);
        notifySuccessful("Successfully added Role.");
      })
      .catch((error) => {
        console.log(error);
        notifyFailed("Could not add role. Please try again");
      });
  };

  const addCustomer = async (customerId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Update",
        "RoleManagement",
        "AddCustomers",
        { customerId: customerId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully added Customer");
    } catch (error) {
      console.error("Error:", error);
      notifyFailed("Could not add Customer. Please try again");
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Delete",
        "RoleManagement",
        "DeleteAccessProfile",
        { customerId: customerId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully deleted Customer");
    } catch (error) {
      console.error("Error:", error);
      notifyFailed("Could not delete Customer. Please try again");
    }
  };

  const addUser = async (userId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Update",
        "RoleManagement",
        "AddUsers",
        { userId: userId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully added User");
    } catch (error) {
      console.error("Error:", error);
      notifyFailed("Could not add User. Please try again");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Delete",
        "RoleManagement",
        "DeleteUserRole",
        { userId: userId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully deleted User");
    } catch (error) {
      console.error("Error:", error);
      notifyFailed("Could not delete User. Please try again");
    }
  };

  const addScreen = async (screenId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Update",
        "RoleManagement",
        "AddPages",
        { pageId: screenId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully added Page");
    } catch (error) {
      notifyFailed("Could not add Page. Please try again");
      console.error("Error:", error);
    }
  };

  const deleteScreen = async (screenId) => {
    try {
      await AxiosHelper.makeAxiosRequest(
        "Delete",
        "RoleManagement",
        "DeletePageProfile",
        { pageId: screenId, roleId: selectedRole },
        null
      );
      notifySuccessful("Successfully deleted Page");
    } catch (error) {
      console.error("Error:", error);
      notifyFailed("Could not delete Page. Please try again");
    }
  };

  const handleCustomerToggle = (customerId) => {
    if (selectedRole === -1 || selectedAdmin) {
      return;
    }
    const updatedCustomers = updateCustomersWithIds([customerId]);
    updatedCustomers.forEach((customer) => {
      if (customer.id === customerId) {
        if (customer.included) {
          addCustomer(customerId);
        } else {
          deleteCustomer(customerId);
        }
      }
    });
  };

  const handleAdminToggle = (roleId, checked) => {
    if (!isAdmin()) {
      return;
    }
    const updatedRoles = roles.map((role) => ({ ...role }));
    updatedRoles.forEach((role) => {
      if (role.id === roleId) {
        role.active = checked;
      }
    });

    if (checked) {
      const updatedCustomers = customers.map((customer) => ({
        ...customer,
        included: true,
      }));

      const updatedScreens = screens.map((screen) => ({
        ...screen,
        included: true,
      }));

      setCustomers(updatedCustomers);
      setScreens(updatedScreens);
      setSelectedAdmin(true);
    } else {
      setSelectedAdmin(false);
    }

    setRoles(updatedRoles);

    AxiosHelper.makeAxiosRequest(
      "Update",
      "RoleManagement",
      "UpdateRoleAdmin",
      { roleId: roleId, isAdmin: checked },
      null
    )
      .then(() => {
        notifySuccessful("Successfully updated role's admin status");
      })
      .catch((error) => {
        console.log(error);
        notifyFailed("Could not make Role admin. Please try again");
      });
  };

  const handleUserToggle = (userId) => {
    if (selectedRole === -1) {
      return;
    }
    const updatedUsers = updateUsersWithIds([userId]);
    updatedUsers.forEach((user) => {
      if (user.id === userId) {
        if (user.included) {
          addUser(userId);
        } else {
          deleteUser(userId);
        }
      }
    });
  };

  const handleScreenToggle = (screenId) => {
    if (selectedRole === -1 || selectedAdmin) {
      return;
    }
    const updatedScreens = updateScreensWithIds([screenId]);
    updatedScreens.forEach((screen) => {
      if (screen.id === screenId) {
        if (screen.included) {
          addScreen(screenId);
        } else {
          deleteScreen(screenId);
        }
      }
    });
  };

  React.useEffect(() => {
    if (selectedRole !== -1) {
      fetchRoleData(selectedRole);
    }
  }, [selectedRole]);

  React.useEffect(() => {
    if (!canAccessPage("Role Management")) {
      navigate("/login");
    }
    const handleRoles = (roles) => {
      let arrRoles = [];
      roles.forEach((role) => {
        let roleElement = {
          id: role.RoleId,
          name: role.RoleName,
          description: role.RoleDescription,
          active: role.RoleAdmin,
        };
        arrRoles.push(roleElement);
      });
      setRoles(arrRoles);
    };

    const handlePages = (pages) => {
      let arrPages = [];
      pages.forEach((page) => {
        let pageElement = {
          id: page.PageId,
          name: page.PageName,
          included: false,
        };
        arrPages.push(pageElement);
      });
      setScreens(arrPages);
    };

    const handleUsers = (users) => {
      let arrUsers = [];
      users.forEach((user) => {
        let userElement = {
          id: user.UserId,
          name: user.FirstName + " " + user.Surname,
          included: false,
        };
        arrUsers.push(userElement);
      });
      setUsers(arrUsers);
    };

    const handleCustomers = (customers) => {
      let arrCustomers = [];
      customers.forEach((customer) => {
        let customerElement = {
          id: customer.CustId,
          name: customer.CustName,
          included: false,
        };
        arrCustomers.push(customerElement);
      });
      setCustomers(arrCustomers);
    };

    const getRolePageData = () => {
      AxiosHelper.makeAxiosRequest(
        "Read",
        "RoleManagement",
        "GetRoleManagementData",
        null,
        null
      )
        .then((response) => {
          handleCustomers(response.data.Customers);
          handleUsers(response.data.Users);
          handlePages(response.data.Pages);
          handleRoles(response.data.Roles);
        })
        .catch((error) => {
          console.log(error);
        });
    };

    getRolePageData();
  }, []);

  return (
    <div>
      <NavBar />
      <ToastContainer />
      <h2 style={{ textAlign: "center", margin: "1rem" }}>Role Management</h2>
      <div className="role-management">
        <Row gutter={16}>
          <Col span={6}>
            <Card
              className="column-card"
              title="Roles"
              style={{ minHeight: "100%" }}
            >
              <Table
                dataSource={roles}
                columns={columns}
                rowKey="id"
                pagination={false}
                style={{ padding: 0, maxHeight: "100%", overflowY: "auto" }}
                footer={() => (
                  <Button
                    onClick={handleAddRole}
                    className=".btn-success .add-button"
                  >
                    Add
                  </Button>
                )}
                onRow={(record) => ({
                  onClick: () => handleSelectRole(record.id),
                  className: record.id === selectedRole ? "selected-row" : "",
                })}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className="column-card"
              title="Customers"
              style={
                selectedRole === -1 || selectedAdmin
                  ? { opacity: "50%" }
                  : { minHeight: "100%" }
              }
            >
              <Table
                dataSource={customers}
                columns={[
                  {
                    title: "Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: (a, b) => a.name.localeCompare(b.name),
                    sortDirections: ["ascend", "descend"],
                  },
                  {
                    title: "Select",
                    key: "select",
                    render: (text, record) => (
                      <Checkbox
                        checked={record.included}
                        onChange={() => handleCustomerToggle(record.id)}
                      />
                    ),
                  },
                ]}
                rowKey="id"
                pagination={false}
                style={{ padding: 0, maxHeight: "100%", overflowY: "auto" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className="column-card"
              title="Users"
              style={
                selectedRole === -1 ? { opacity: "50%" } : { minHeight: "100%" }
              }
            >
              <Table
                dataSource={users}
                columns={[
                  {
                    title: "Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: (a, b) => a.name.localeCompare(b.name),
                    sortDirections: ["ascend", "descend"],
                  },
                  {
                    title: "Select",
                    key: "select",
                    render: (text, record) => (
                      <Checkbox
                        checked={record.included}
                        onChange={() => handleUserToggle(record.id)}
                      />
                    ),
                  },
                ]}
                rowKey="id"
                pagination={false}
                style={{ padding: 0, maxHeight: "100%", overflowY: "auto" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card
              className="column-card"
              title="Screens"
              style={
                selectedRole === -1 || selectedAdmin
                  ? { opacity: "50%" }
                  : { minHeight: "100%" }
              }
            >
              <Table
                dataSource={screens}
                columns={[
                  {
                    title: "Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: (a, b) => a.name.localeCompare(b.name),
                    sortDirections: ["ascend", "descend"],
                    render: (text, record) => <span>{record.name}</span>,
                  },
                  {
                    title: "Select",
                    key: "select",
                    render: (text, record) => (
                      <Checkbox
                        checked={record.included}
                        onChange={() => handleScreenToggle(record.id)}
                      />
                    ),
                  },
                ]}
                rowKey="id"
                pagination={false}
                style={{ padding: 0, maxHeight: "100%", overflowY: "auto" }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default RoleManagementPage;
